import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, throwError, timer } from 'rxjs';
import { catchError, mergeMap, retryWhen, tap } from 'rxjs/operators';
import { THROTTLING_CONFIG } from './throttling-interceptor.config';

@Injectable()
export class ThrottlingInterceptor implements HttpInterceptor {
  private readonly config = inject(THROTTLING_CONFIG);
  private readonly activeRequests = new Set<Observable<HttpEvent<unknown>>>();
  private readonly requestQueue: Array<() => void> = [];

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    return this.throttleRequest(() => this.executeWithRetry(req, next));
  }

  private throttleRequest<T>(requestFn: () => Observable<T>): Observable<T> {
    if (this.activeRequests.size < this.config.maxConcurrentRequests) {
      return this.executeRequest(requestFn);
    }

    // Queue the request if we've reached the limit
    return new Observable<T>((subscriber) => {
      this.requestQueue.push(() => {
        this.executeRequest(requestFn).subscribe(subscriber);
      });
    });
  }

  private executeRequest<T>(requestFn: () => Observable<T>): Observable<T> {
    const request$ = requestFn();

    this.activeRequests.add(request$ as Observable<HttpEvent<unknown>>);

    return request$.pipe(
      tap({
        complete: () => this.onRequestComplete(),
        error: () => this.onRequestComplete(),
        finalize: () =>
          this.activeRequests.delete(
            request$ as Observable<HttpEvent<unknown>>,
          ),
      }),
    );
  }

  private onRequestComplete(): void {
    // Process the next request in the queue
    const nextRequest = this.requestQueue.shift();

    if (nextRequest) {
      nextRequest();
    }

    // Clean up active requests (this is handled by the tap operator above)
    // We don't need to explicitly remove from activeRequests as each request
    // is unique and will be garbage collected
  }

  private executeWithRetry(
    req: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    let retryCount = 0;

    return next.handle(req).pipe(
      retryWhen((errors) =>
        errors.pipe(
          mergeMap((error: HttpErrorResponse) => {
            // Only retry on HTTP 429 (Too Many Requests)
            if (error.status !== 429) {
              return throwError(() => error);
            }

            // Check if we've exceeded max retries
            if (retryCount >= (this.config.maxRetries || 3)) {
              return throwError(() => error);
            }

            retryCount++;

            // Calculate delay from Retry-After header or use base delay
            const delay = this.getRetryDelay(error);

            console.warn(
              `HTTP 429 received. Retrying in ${delay}ms (attempt ${retryCount}/${this.config.maxRetries})`,
            );

            return timer(delay);
          }),
        ),
      ),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 429) {
          console.error(
            `Request failed after ${retryCount} retries due to rate limiting (HTTP 429)`,
          );
        }

        return throwError(() => error);
      }),
    );
  }

  private getRetryDelay(error: HttpErrorResponse): number {
    const retryAfterHeader = error.headers.get('Retry-After');

    if (!retryAfterHeader) {
      return this.config.baseRetryDelay || 1000;
    }

    // Retry-After can be in seconds or HTTP date format
    const retryAfterSeconds = parseInt(retryAfterHeader, 10);

    if (!isNaN(retryAfterSeconds)) {
      // It's a number of seconds
      return retryAfterSeconds * 1000;
    }

    // It might be an HTTP date format
    const retryAfterDate = new Date(retryAfterHeader);
    const now = new Date();

    if (retryAfterDate.getTime() > now.getTime()) {
      return retryAfterDate.getTime() - now.getTime();
    }

    // Fallback to base delay if we can't parse the header
    return this.config.baseRetryDelay || 1000;
  }
}
